import { useState, useEffect, useRef, useCallback } from "react";
import {
  Timer, Play, Pause, ArrowCounterClockwise, X, Bell, BellSlash,
} from "@phosphor-icons/react";

/**
 * RestTimer — wall-clock accurate.
 *
 * Why the rewrite: browsers throttle / suspend JS timers when the tab is
 * hidden or the device is locked. A naïve `setInterval` that decrements a
 * counter drifts badly (tens of seconds) and can stop entirely.
 *
 * Strategy:
 *  - When running, we store the absolute UNIX end timestamp (`endTs`).
 *  - Every tick we recompute `remaining = endTs - Date.now()`.
 *    Throttled ticks are harmless: we read the wall clock each time, so the
 *    value is always correct no matter how long the JS loop was paused.
 *  - On `visibilitychange` / `focus` / `pageshow` we force an immediate
 *    re-sync so the UI catches up the instant the user returns.
 *  - State is persisted to localStorage so navigation / tab-restore resumes
 *    the exact remaining time.
 *  - On supported browsers we schedule an OS notification via the
 *    `TimestampTrigger` API so it fires even with the tab closed / screen
 *    locked. Otherwise we fire a "catch-up" notification + beep as soon as
 *    the user returns.
 */

const PRESETS = [60, 90, 120];
const DEFAULT_DURATION = 90; // seconds
const STORAGE_KEY = "fitpro-rest-timer-state";
const TICK_MS = 200; // UI cadence; accuracy comes from Date.now()
const NOTIFICATIONS_SUPPORTED =
  typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator;
const PUSH_SUPPORTED =
  typeof window !== "undefined" && "PushManager" in window && "serviceWorker" in navigator;
const NOTIF_TAG = "fitpro-rest-timer";
const API = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL)
  ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** Ensure the browser is subscribed to Web Push for this shared token, then
 *  resolve. Safe to call multiple times — it's a no-op once subscribed. */
async function ensureSharedPushSubscription(shareToken) {
  if (!shareToken) return false;
  if (!PUSH_SUPPORTED || !NOTIFICATIONS_SUPPORTED) return false;
  if (Notification.permission !== "granted") return false;
  try {
    let reg = await navigator.serviceWorker.getRegistration("/sw.js");
    if (!reg) reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const keyResp = await fetch(`${API}/shared/${shareToken}/push/public-key`);
      if (!keyResp.ok) return false;
      const { public_key: publicKey } = await keyResp.json();
      if (!publicKey) return false;
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(publicKey),
      });
    }
    const resp = await fetch(`${API}/shared/${shareToken}/push/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subscription: sub.toJSON ? sub.toJSON() : sub }),
    });
    return resp.ok;
  } catch (err) {
    console.warn("ensureSharedPushSubscription failed:", err);
    return false;
  }
}

async function scheduleServerRestPush(shareToken, endTs) {
  if (!shareToken) return false;
  try {
    const resp = await fetch(`${API}/shared/${shareToken}/rest-timer/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ end_ts_ms: endTs }),
    });
    return resp.ok;
  } catch (err) {
    console.warn("scheduleServerRestPush failed:", err);
    return false;
  }
}

async function cancelServerRestPush(shareToken) {
  if (!shareToken) return;
  try {
    await fetch(`${API}/shared/${shareToken}/rest-timer/cancel`, { method: "POST" });
  } catch (err) {
    console.warn("cancelServerRestPush failed:", err);
  }
}

function formatTime(s) {
  const total = Math.max(0, Math.round(s));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

async function ensureServiceWorker() {
  if (!NOTIFICATIONS_SUPPORTED) return null;
  try {
    let reg = await navigator.serviceWorker.getRegistration("/sw.js");
    if (!reg) reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    await navigator.serviceWorker.ready;
    return reg;
  } catch (err) {
    console.warn("ensureServiceWorker failed:", err);
    return null;
  }
}

async function showEndNotificationNow(returnUrl) {
  if (!NOTIFICATIONS_SUPPORTED) return false;
  if (Notification.permission !== "granted") return false;
  const reg = await ensureServiceWorker();
  const options = {
    body: "Torna all'esercizio!",
    icon: "/logo.png",
    badge: "/logo.png",
    tag: NOTIF_TAG,
    renotify: true,
    vibrate: [200, 80, 400],
    silent: false,
    requireInteraction: false,
    data: { url: returnUrl || "/", type: "rest-timer-end" },
  };
  try {
    if (reg && reg.showNotification) {
      await reg.showNotification("Recupero completato", options);
    } else {
      new Notification("Recupero completato", options);
    }
    return true;
  } catch (err) {
    console.warn("showNotification failed:", err);
    return false;
  }
}

/** Deprecated: the client-side TimestampTrigger API has been removed from
 *  Chrome and never shipped elsewhere, so we rely on a server-scheduled
 *  Web Push (see `scheduleServerRestPush`). This function is a no-op kept
 *  only to not break any legacy caller during refactor. */
async function scheduleEndNotificationAt() { return false; }

async function cancelScheduledEndNotification(regArg) {
  if (!NOTIFICATIONS_SUPPORTED) return;
  try {
    const reg = regArg || (await ensureServiceWorker());
    if (!reg || !reg.getNotifications) return;
    const list = await reg.getNotifications({ tag: NOTIF_TAG });
    list.forEach((n) => { try { n.close(); } catch (err) { console.debug("[RestTimer]", err); } });
  } catch (err) { console.debug("[RestTimer]", err); }
}

function loadPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || typeof s !== "object") return null;
    return s;
  } catch { return null; }
}

export function RestTimer({ shareToken } = {}) {
  // Rehydrate once synchronously to avoid a visible flash of 01:30.
  const persisted = typeof window !== "undefined" ? loadPersisted() : null;
  const initialDuration = typeof persisted?.duration === "number"
    ? persisted.duration : DEFAULT_DURATION;
  const initialEndTs = typeof persisted?.endTs === "number" ? persisted.endTs : null;
  const initialPausedMs = typeof persisted?.pausedRemainingMs === "number"
    ? persisted.pausedRemainingMs
    : initialDuration * 1000;

  const [duration, setDuration] = useState(initialDuration);
  // When running: endTs is the absolute UNIX ms of completion; else null.
  const [endTs, setEndTs] = useState(initialEndTs && initialEndTs > Date.now() ? initialEndTs : null);
  // When paused / idle: the remaining milliseconds to resume with.
  const [pausedRemainingMs, setPausedRemainingMs] = useState(() => {
    if (initialEndTs) {
      const rem = initialEndTs - Date.now();
      if (rem > 0) return 0;
      return 0; // finished while away
    }
    return initialPausedMs;
  });
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [flashEnd, setFlashEnd] = useState(false);
  const [open, setOpen] = useState(false);
  const [notifPerm, setNotifPerm] = useState(
    NOTIFICATIONS_SUPPORTED ? Notification.permission : "unsupported"
  );

  const firedEndRef = useRef(Boolean(persisted?.fired));
  const returnUrlRef = useRef(persisted?.returnUrl || null);
  const audioCtxRef = useRef(null);
  const intervalRef = useRef(null);

  const running = endTs !== null;
  const remainingMs = running
    ? Math.max(0, endTs - nowTs)
    : Math.max(0, pausedRemainingMs);
  const remainingSec = Math.ceil(remainingMs / 1000);

  // ---- Beep + vibration + foreground notification ----
  const playBeep = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx) {
        if (ctx.state === "suspended") ctx.resume();
        const beep = (when, freq, dur) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0, when);
          gain.gain.linearRampToValueAtTime(0.35, when + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.0001, when + dur);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(when);
          osc.stop(when + dur + 0.02);
        };
        const t = ctx.currentTime;
        beep(t, 880, 0.18);
        beep(t + 0.22, 1320, 0.28);
      }
    } catch (err) { console.debug("[RestTimer]", err); }
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate([200, 80, 400]);
      }
    } catch (err) { console.debug("[RestTimer]", err); }
    // Foreground catch-up notification — shown when the user is still in
    // the tab at end-of-timer. The lock-screen/backgrounded case is handled
    // by the server-scheduled Web Push instead.
    showEndNotificationNow(returnUrlRef.current);
  }, []);

  const requestNotifPerm = useCallback(async () => {
    if (!NOTIFICATIONS_SUPPORTED) return;
    try {
      const perm = await Notification.requestPermission();
      setNotifPerm(perm);
      if (perm === "granted") {
        ensureServiceWorker();
        // Subscribe this browser to server-sent push so lock-screen
        // notifications can arrive even when the tab is suspended.
        ensureSharedPushSubscription(shareToken);
      }
    } catch (err) {
      console.warn("Notification.requestPermission failed:", err);
    }
  }, [shareToken]);

  // ---- UI tick (accuracy comes from Date.now()) ----
  useEffect(() => {
    if (!running) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }
    intervalRef.current = setInterval(() => setNowTs(Date.now()), TICK_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [running]);

  // ---- Re-sync instantly on tab focus / visibility change / bfcache restore ----
  useEffect(() => {
    const sync = () => setNowTs(Date.now());
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("focus", sync);
    window.addEventListener("pageshow", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("focus", sync);
      window.removeEventListener("pageshow", sync);
    };
  }, []);

  // Subscribe to server-sent push early (if permission already granted from
  // a previous visit). Safe to call multiple times.
  useEffect(() => {
    if (!shareToken) return;
    if (NOTIFICATIONS_SUPPORTED && Notification.permission === "granted") {
      ensureSharedPushSubscription(shareToken);
    }
  }, [shareToken]);

  // ---- End-of-timer detection (runs on every nowTs tick while running) ----
  useEffect(() => {
    if (!running) return;
    if (endTs - nowTs > 0) return;
    // Countdown reached zero.
    setEndTs(null);
    setPausedRemainingMs(0);
    if (!firedEndRef.current) {
      firedEndRef.current = true;
      setFlashEnd(true);
      setTimeout(() => setFlashEnd(false), 1500);
      playBeep();
    }
  }, [running, endTs, nowTs, playBeep]);

  // ---- Catch-up: timer ended while we were not mounted / tab was closed ----
  useEffect(() => {
    if (!running && pausedRemainingMs === 0 && !firedEndRef.current && persisted?.endTs) {
      firedEndRef.current = true;
      setFlashEnd(true);
      setTimeout(() => setFlashEnd(false), 1500);
      playBeep();
    }
    // We intentionally run this only once on mount with the rehydrated state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Persist state ----
  useEffect(() => {
    try {
      const isPristine =
        !running &&
        pausedRemainingMs === duration * 1000 &&
        !firedEndRef.current;
      if (isPristine) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          duration,
          endTs,
          pausedRemainingMs,
          fired: firedEndRef.current,
          returnUrl: returnUrlRef.current,
        }));
      }
    } catch (err) { console.debug("[RestTimer] storage unavailable (Safari private?)", err); }
  }, [duration, endTs, pausedRemainingMs, running, flashEnd]);

  // ---- Actions ----
  const start = () => {
    if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume();
    const remMs = pausedRemainingMs > 0 ? pausedRemainingMs : duration * 1000;
    const newEnd = Date.now() + remMs;
    // Capture the exact URL the user was on when they hit Play, so tapping the
    // end-of-timer notification brings them right back to the same view.
    try {
      returnUrlRef.current =
        window.location.pathname + window.location.search + window.location.hash;
    } catch { returnUrlRef.current = "/"; }
    firedEndRef.current = false;
    setPausedRemainingMs(0);
    setEndTs(newEnd);
    setNowTs(Date.now());
    // Legacy local notification scheduler — kept as a no-op for safety.
    scheduleEndNotificationAt(newEnd, returnUrlRef.current);
    // Server-scheduled Web Push so the notification arrives on the lock
    // screen / backgrounded tab. Best-effort — if the user hasn't granted
    // permission yet this silently returns false.
    ensureSharedPushSubscription(shareToken).then((ok) => {
      if (ok) scheduleServerRestPush(shareToken, newEnd);
    });
  };

  const pause = () => {
    const remMs = Math.max(0, (endTs || 0) - Date.now());
    setEndTs(null);
    setPausedRemainingMs(remMs);
    cancelScheduledEndNotification();
    cancelServerRestPush(shareToken);
  };

  const reset = () => {
    setEndTs(null);
    setPausedRemainingMs(duration * 1000);
    setFlashEnd(false);
    firedEndRef.current = false;
    cancelScheduledEndNotification();
    cancelServerRestPush(shareToken);
  };

  const applyPreset = (s) => {
    setDuration(s);
    setEndTs(null);
    setPausedRemainingMs(s * 1000);
    setFlashEnd(false);
    firedEndRef.current = false;
    cancelScheduledEndNotification();
    cancelServerRestPush(shareToken);
  };

  const pct = duration > 0
    ? Math.max(0, Math.min(100, (remainingMs / (duration * 1000)) * 100))
    : 0;

  return (
    <div className="relative" data-testid="rest-timer">
      {/* Compact pill */}
      <div
        className={`flex items-center gap-1.5 bg-[#1A1A1A] border rounded-full pl-2.5 pr-1 py-1 transition-colors ${
          flashEnd ? "border-[#34C759]" : running ? "border-[#007AFF]/50" : "border-[#262626]"
        }`}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 outline-none"
          aria-label="Apri timer recupero"
          data-testid="rest-timer-toggle"
        >
          <Timer
            size={13}
            weight="fill"
            className={flashEnd ? "text-[#34C759]" : running ? "text-[#007AFF]" : "text-zinc-400"}
          />
          <span className="text-[11px] font-mono font-bold text-white tabular-nums leading-none">
            {formatTime(remainingSec)}
          </span>
        </button>
        <button
          type="button"
          onClick={() => (running ? pause() : start())}
          className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
            running ? "bg-[#FF9500]" : "bg-[#007AFF]"
          } text-white active:scale-95`}
          aria-label={running ? "Pausa recupero" : "Avvia recupero"}
          data-testid="rest-timer-playpause"
        >
          {running ? <Pause size={10} weight="fill" /> : <Play size={10} weight="fill" />}
        </button>
      </div>

      {/* Expanded panel */}
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            data-testid="rest-timer-backdrop"
          />
          <div
            className="absolute right-0 top-full mt-2 w-56 bg-[#171717] border border-[#262626] rounded-xl p-3 z-50 shadow-2xl"
            data-testid="rest-timer-panel"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Timer recupero</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-zinc-500 hover:text-white"
                aria-label="Chiudi"
                data-testid="rest-timer-close"
              >
                <X size={12} weight="bold" />
              </button>
            </div>

            <div className="text-center mb-3">
              <p className="font-mono text-3xl font-bold text-white tabular-nums leading-none">
                {formatTime(remainingSec)}
              </p>
              <div className="h-1 bg-[#262626] rounded-full mt-2.5 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#007AFF] to-[#34C759] transition-all duration-300"
                  style={{ width: `${pct}%` }}
                  data-testid="rest-timer-bar"
                />
              </div>
            </div>

            <div className="flex gap-1.5 mb-2.5">
              <button
                type="button"
                onClick={() => (running ? pause() : start())}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                  running
                    ? "bg-[#FF9500] hover:bg-[#FFAD33] text-white"
                    : "bg-[#007AFF] hover:bg-[#3395FF] text-white"
                }`}
                data-testid="rest-timer-playpause-main"
              >
                {running ? (
                  <><Pause size={12} weight="fill" /> Pausa</>
                ) : (
                  <><Play size={12} weight="fill" /> Play</>
                )}
              </button>
              <button
                type="button"
                onClick={reset}
                className="px-3 py-2 bg-[#1A1A1A] border border-[#262626] hover:bg-[#262626] text-white rounded-lg"
                aria-label="Reset"
                data-testid="rest-timer-reset"
              >
                <ArrowCounterClockwise size={13} weight="bold" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className={`py-1.5 rounded-lg text-[11px] font-bold transition-colors ${
                    duration === p
                      ? "bg-[#D4FF00] text-[#0A0A0A]"
                      : "bg-[#1A1A1A] border border-[#262626] text-zinc-300 hover:bg-[#262626]"
                  }`}
                  data-testid={`rest-timer-preset-${p}`}
                >
                  {p}s
                </button>
              ))}
            </div>

            {NOTIFICATIONS_SUPPORTED && notifPerm !== "granted" && (
              <button
                type="button"
                onClick={requestNotifPerm}
                disabled={notifPerm === "denied"}
                className={`mt-2.5 w-full py-1.5 rounded-lg text-[10px] font-medium flex items-center justify-center gap-1.5 border transition-colors ${
                  notifPerm === "denied"
                    ? "bg-[#FF3B30]/10 border-[#FF3B30]/20 text-[#FF3B30]/80 cursor-not-allowed"
                    : "bg-[#1A1A1A] border-[#262626] text-zinc-400 hover:text-white hover:border-[#007AFF]/40"
                }`}
                data-testid="rest-timer-enable-notif"
                title={notifPerm === "denied" ? "Permesso negato dal browser" : "Abilita notifiche anche a schermo bloccato"}
              >
                {notifPerm === "denied" ? <BellSlash size={11} weight="fill" /> : <Bell size={11} weight="fill" />}
                {notifPerm === "denied" ? "Notifiche bloccate" : "Abilita notifiche fine timer"}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
